import React, { useState, useContext, useEffect } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import ReactHtmlParser from "react-html-parser";
import Select from "react-select";
import { AuthContext } from "../../context/AuthContext";
import JobFilterForm from "../../component/JobFilterForm";
import JobItem from "../../component/JobItem";
import { generateRandomString } from "../../Utility/Utility";

const customReactSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    height: '46px',
    borderRadius: '6px',
    borderColor: state.isFocused ? '#2e3192' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(46, 49, 146, 0.1)' : 'none',
    '&:hover': {
      borderColor: state.isFocused ? '#2e3192' : '#d1d5db',
    },
    fontSize: '0.95rem',
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 8px 0 15px',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#9ca3af',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: '#6b7280',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  })
};

export default function Index() {
  const history = useHistory();
  const [aboutArray, setAboutArray] = useState([]);
  const [organizationArray, setOrganizationArray] = useState([]);
  const [homejobsArray, setHomejobsArray] = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const { isAuthenticated, userType } = useContext(AuthContext);
  const [paginationData, setPaginationData] = useState({
    current_page: 0,
    from: 0,
    to: 0,
    per_page: 0,
    last_page: 0,
    total: 0,
    first_page_url: "",
    prev_page_url: "",
    last_page_url: "",
    next_page_url: "",
    path: "",
  });

  const [searchParams, setSearchParams] = useState({});
  const [isScrollVisible, setIsScrollVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [worktypes, setWorktypes] = useState([]);
  const [jobtypes, setJobtypes] = useState([]);
  const location = useLocation();

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Top Search Filter states
  const [categories, setCategories] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [topKeyword, setTopKeyword] = useState("");
  const [topLocation, setTopLocation] = useState("");
  const [topCategory, setTopCategory] = useState("");
  const [topCompany, setTopCompany] = useState("");

  // ─── Effect: Fetch filter data for top selects ─────────────────────────────
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    axios
      .get(constvar.api_url + "home/job_filter_data", {
        signal: controller.signal,
      })
      .then((response) => {
        if (!isMounted) return;
        if (response.data.status === "success") {
          setCategories(response.data.job_category || []);
          setCompanies(response.data.companies || []);
          setWorktypes(response.data.worktype || []);
          setJobtypes(response.data.jobtype || []);
        }
      })
      .catch((err) => {
        if (!isMounted) return;
        if (axios.isCancel(err)) return;
        console.error("Failed to load top search filter data:", err);
      });

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  // ─── Effect 0: Read URL search params on mount & sync top states ──────────
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const initialParams = {};
    
    params.forEach((value, key) => {
      initialParams[key] = value;
    });
    
    setSearchParams(initialParams);
    setTopKeyword(initialParams.keyword || "");
    setTopLocation(initialParams.location || "");
    setTopCategory(initialParams.job_category || "");
    setTopCompany(initialParams.company || "");
  }, [location.search]);


  // ─── Effect 1: Fetch frontend static data (about, organization) ───────────
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    axios
      .get(constvar.api_url + "home/get_frontend_data", {
        signal: controller.signal,
      })
      .then((response) => {
        if (!isMounted) return;
        if (response.data.status == "success") {
          setAboutArray(response.data.about);
          setOrganizationArray(response.data.organization);
        } else {
          setAboutArray([]);
          setOrganizationArray([]);
        }
      })
      .catch((err) => {
        if (!isMounted) return;
        if (axios.isCancel(err)) return; // request was aborted, ignore
        setAboutArray([]);
        setOrganizationArray([]);
      });

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  // ─── Effect 2: Fetch jobs when searchParams changes ────────────────────────
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchJobs = async () => {
      setIsLoading(true);
      try {
        var query = new URLSearchParams(searchParams);
        var url = constvar.api_url + "jobs?" + query.toString();
        const response = await axios.get(url, { signal: controller.signal });

        if (!isMounted) return;

        if (response.data.status == "success") {
          var result = response.data.data;
          setHomejobsArray(result.data);
          delete result.data;
          setPaginationData({ ...result });
          setTotalRecord(result.total);
        } else {
          setHomejobsArray([]);
          setTotalRecord(0);
        }
      } catch (err) {
        if (!isMounted) return;
        if (axios.isCancel(err)) return; // aborted, ignore
        setHomejobsArray([]);
        setTotalRecord(0);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchJobs();

    return () => {
      isMounted = false;
      controller.abort();
      setHomejobsArray([]);
      setTotalRecord(0);
    };
  }, [searchParams]);

  // ─── Effect 3: Scroll-to-top button visibility ────────────────────────────
  useEffect(() => {
    let isMounted = true;

    const toggleVisibility = () => {
      if (!isMounted) return;
      if (window.scrollY > 300) {
        setIsScrollVisible(true);
      } else {
        setIsScrollVisible(false);
      }
    };

    window.addEventListener("scroll", toggleVisibility);

    return () => {
      isMounted = false;
      window.removeEventListener("scroll", toggleVisibility);
    };
  }, []);

  const handlePageChange = (pageNumber) => {
    const params = new URLSearchParams(location.search);
    params.set("page", pageNumber);
    history.push(`/job_search?${params.toString()}`);
    window.scrollTo({ top: 300, behavior: "smooth" });
  };

  const removeFilter = (key, value = null) => {
    const params = new URLSearchParams(location.search);
    
    if (key === "typeOfWork") {
      const values = params.get("typeOfWork") ? params.get("typeOfWork").split(",") : [];
      const updatedValues = values.filter(v => v !== value.toString());
      if (updatedValues.length > 0) {
        params.set("typeOfWork", updatedValues.join(","));
      } else {
        params.delete("typeOfWork");
      }
    } else if (key === "jobType") {
      const values = params.get("jobType") ? params.get("jobType").split(",") : [];
      const updatedValues = values.filter(v => v !== value.toString());
      if (updatedValues.length > 0) {
        params.set("jobType", updatedValues.join(","));
      } else {
        params.delete("jobType");
      }
    } else {
      params.delete(key);
      if (key === "keyword") setTopKeyword("");
      if (key === "location") setTopLocation("");
      if (key === "job_category") setTopCategory("");
      if (key === "company") setTopCompany("");
    }
    
    params.delete("page");
    history.push(`/job_search?${params.toString()}`);
  };

  const clearAllFilters = () => {
    setTopKeyword("");
    setTopLocation("");
    setTopCategory("");
    setTopCompany("");
    history.push("/job_search");
  };

  const renderActiveFilters = () => {
    const badges = [];

    if (searchParams.keyword) {
      badges.push({
        key: "keyword",
        label: `Keyword: ${searchParams.keyword}`,
        onClick: () => removeFilter("keyword")
      });
    }

    if (searchParams.location) {
      badges.push({
        key: "location",
        label: `Location: ${searchParams.location}`,
        onClick: () => removeFilter("location")
      });
    }

    if (searchParams.job_category) {
      const cat = categories.find(c => c.id.toString() === searchParams.job_category.toString());
      if (cat) {
        badges.push({
          key: "job_category",
          label: `Category: ${cat.title}`,
          onClick: () => removeFilter("job_category")
        });
      }
    }

    if (searchParams.company) {
      const comp = companies.find(c => c.id.toString() === searchParams.company.toString());
      if (comp) {
        badges.push({
          key: "company",
          label: `Company: ${comp.title}`,
          onClick: () => removeFilter("company")
        });
      }
    }

    if (searchParams.typeOfWork) {
      const ids = searchParams.typeOfWork.split(",");
      ids.forEach(id => {
        const wt = worktypes.find(w => w.id.toString() === id.toString());
        if (wt) {
          badges.push({
            key: "typeOfWork",
            value: id,
            label: wt.title,
            onClick: () => removeFilter("typeOfWork", id)
          });
        }
      });
    }

    if (searchParams.jobType) {
      const ids = searchParams.jobType.split(",");
      ids.forEach(id => {
        const jt = jobtypes.find(j => j.id.toString() === id.toString());
        if (jt) {
          badges.push({
            key: "jobType",
            value: id,
            label: jt.title,
            onClick: () => removeFilter("jobType", id)
          });
        }
      });
    }

    if (searchParams.postDate && searchParams.postDate !== "All") {
      badges.push({
        key: "postDate",
        label: `Posted: ${searchParams.postDate}`,
        onClick: () => removeFilter("postDate")
      });
    }

    if (badges.length === 0) return null;

    return (
      <div className="active-filters-container d-flex flex-wrap align-items-center m-b20" style={{ gap: '10px', padding: '10px 0' }}>
        {badges.map((badge, index) => (
          <span 
            key={index} 
            className="active-filter-badge d-inline-flex align-items-center"
            style={{
              backgroundColor: '#f1f3f9',
              color: '#2e3192',
              padding: '6px 12px',
              borderRadius: '20px',
              fontSize: '0.85rem',
              fontWeight: '500',
              border: '1px solid #d1d5db',
              gap: '6px'
            }}
          >
            {badge.label}
            <button 
              type="button" 
              onClick={badge.onClick}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0',
                fontSize: '0.95rem',
                color: '#6b7280',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <i className="ri-close-line"></i>
            </button>
          </span>
        ))}
        <button 
          type="button" 
          onClick={clearAllFilters}
          className="clear-all-filters-btn"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#2e3192',
            fontSize: '0.85rem',
            fontWeight: '600',
            padding: '4px 8px'
          }}
        >
          Clear All
        </button>
      </div>
    );
  };

  const renderPagination = () => {
    const { current_page, last_page, from, to, total } = paginationData;
    if (!total || last_page <= 1) return null;

    const pages = [];
    const range = 1; // Number of pages to show around current page

    pages.push(1);

    if (current_page - range > 2) {
      pages.push("...");
    }

    for (let i = Math.max(2, current_page - range); i <= Math.min(last_page - 1, current_page + range); i++) {
      pages.push(i);
    }

    if (current_page + range < last_page - 1) {
      pages.push("...");
    }

    if (last_page > 1) {
      pages.push(last_page);
    }

    return (
      <div className="pagination-outer d-flex flex-wrap align-items-center justify-content-between p-t30 p-b30" style={{ borderTop: '1px solid #eee', marginTop: '20px' }}>
        <div className="pagination-info text-muted">
          Showing {from} - {to} of {total} jobs
        </div>
        <ul className="pagination-list d-flex align-items-center m-0 p-0" style={{ listStyle: 'none', gap: '8px' }}>
          <li>
            <button
              onClick={() => current_page > 1 && handlePageChange(current_page - 1)}
              disabled={current_page === 1}
              className="pagination-btn pagination-prev-next"
              style={{
                padding: '6px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: '#fff',
                cursor: current_page === 1 ? 'not-allowed' : 'pointer',
                color: current_page === 1 ? '#9ca3af' : '#4a5565',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <i className="ri-arrow-left-s-line"></i> Previous
            </button>
          </li>
          {pages.map((p, idx) => (
            <li key={idx}>
              {p === "..." ? (
                <span style={{ padding: '6px 12px', color: '#6b7280' }}>...</span>
              ) : (
                <button
                  onClick={() => handlePageChange(p)}
                  className={`pagination-btn pagination-number ${current_page === p ? "active" : ""}`}
                  style={{
                    padding: '6px 12px',
                    border: current_page === p ? '1px solid #2e3192' : '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: current_page === p ? '#2e3192' : '#fff',
                    color: current_page === p ? '#fff' : '#4a5565',
                    cursor: 'pointer',
                    fontWeight: current_page === p ? 'bold' : 'normal',
                    fontSize: '0.9rem',
                    minWidth: '36px',
                    textAlign: 'center'
                  }}
                >
                  {p}
                </button>
              )}
            </li>
          ))}
          <li>
            <button
              onClick={() => current_page < last_page && handlePageChange(current_page + 1)}
              disabled={current_page === last_page}
              className="pagination-btn pagination-prev-next"
              style={{
                padding: '6px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: '#fff',
                cursor: current_page === last_page ? 'not-allowed' : 'pointer',
                color: current_page === last_page ? '#9ca3af' : '#4a5565',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              Next <i className="ri-arrow-right-s-line"></i>
            </button>
          </li>
        </ul>
      </div>
    );
  };

  const getActiveFiltersString = () => {
    const parts = [];
    if (searchParams.keyword) {
      parts.push(`Keyword: "${searchParams.keyword}"`);
    }
    if (searchParams.location) {
      parts.push(`Location: "${searchParams.location}"`);
    }
    if (searchParams.job_category) {
      const match = categories.find(c => c.id.toString() === searchParams.job_category.toString());
      if (match) {
        parts.push(`Category: "${match.title}"`);
      }
    }
    if (searchParams.company) {
      const match = companies.find(c => c.id.toString() === searchParams.company.toString());
      if (match) {
        parts.push(`Company: "${match.title}"`);
      }
    }
    return parts.join(", ");
  };

  const handleTopSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (topKeyword) params.append("keyword", topKeyword);
    if (topLocation) params.append("location", topLocation);
    if (topCategory) params.append("job_category", topCategory);
    if (topCompany) params.append("company", topCompany);
    
    // Maintain sidebar filters
    if (searchParams.typeOfWork) params.append("typeOfWork", searchParams.typeOfWork);
    if (searchParams.jobType) params.append("jobType", searchParams.jobType);
    if (searchParams.postDate) params.append("postDate", searchParams.postDate);
    
    history.push(`/job_search?${params.toString()}`);
  };

  const categoryOptions = categories.map((cat) => ({
    value: cat.id.toString(),
    label: cat.title,
  }));

  const companyOptions = companies.map((comp) => ({
    value: comp.id.toString(),
    label: comp.title,
  }));

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <React.Fragment>
      <div className="section-full p-t60  p-b60 site-bg-light">
        <div className="sf-premium-search-sec">
          <div className="container">
            <h2 className="sf-premium-search-title">Find Your Dream Job</h2>
            <p className="sf-premium-search-subtitle">
              Discover thousands of opportunities from top Companies
            </p>
            <div className="sf-premium-search-card">
              <form onSubmit={handleTopSearch}>
                <div className="sf-premium-search-grid">
                  
                  {/* Job Title Field */}
                  <div className="sf-premium-search-field">
                    <label className="sf-premium-search-label">Job Title</label>
                    <div className="sf-premium-search-input-wrapper">
                      <input
                        type="text"
                        className="sf-premium-search-input"
                        placeholder="Type job title"
                        value={topKeyword}
                        onChange={(e) => setTopKeyword(e.target.value)}
                      />
                      {/* <i className="ri-arrow-down-s-line sf-premium-search-chevron"></i> */}
                    </div>
                  </div>

                  {/* Location Field */}
                  <div className="sf-premium-search-field">
                    <label className="sf-premium-search-label">Location</label>
                    <div className="sf-premium-search-input-wrapper">
                      <input
                        type="text"
                        className="sf-premium-search-input"
                        placeholder="Type location"
                        value={topLocation}
                        onChange={(e) => setTopLocation(e.target.value)}
                      />
                      {/* <i className="ri-arrow-down-s-line sf-premium-search-chevron"></i> */}
                    </div>
                  </div>

                  {/* Category Select */}
                  <div className="sf-premium-search-field">
                    <label className="sf-premium-search-label">Category</label>
                    <Select
                      placeholder="Select Category"
                      isSearchable={true}
                      isClearable={true}
                      options={categoryOptions}
                      value={categoryOptions.find(opt => opt.value === topCategory) || null}
                      onChange={(selectedOption) => {
                        setTopCategory(selectedOption ? selectedOption.value : "");
                      }}
                      styles={customReactSelectStyles}
                    />
                  </div>

                  {/* Company Select */}
                  <div className="sf-premium-search-field">
                    <label className="sf-premium-search-label">Company</label>
                    <Select
                      placeholder="Select Company"
                      isSearchable={true}
                      isClearable={true}
                      options={companyOptions}
                      value={companyOptions.find(opt => opt.value === topCompany) || null}
                      onChange={(selectedOption) => {
                        setTopCompany(selectedOption ? selectedOption.value : "");
                      }}
                      styles={customReactSelectStyles}
                    />
                  </div>

                </div>
                <button type="submit" className="sf-premium-search-btn">
                  <i className="ri-search-line"></i>
                  <span>Search Jobs</span>
                </button>
              </form>
            </div>
          </div>
        </div>
        
        <div className="container">
          <div className="mobile_filter_icon" onClick={() => setIsFilterMenuOpen(true)}>
            <i className="ri-list-check"></i> 
          </div>
          <div className="row">
            <div className="col-xl-3 col-lg-4 col-md-12 rightSidebar">
              <div className="side-bar">
                <div className="sidebar-elements search-bx">
                  <JobFilterForm 
                    searchParams={searchParams}
                    initialTypeOfWork={searchParams.typeOfWork || ""}
                    initialJobType={searchParams.jobType || ""}
                    initialPostDate={searchParams.postDate || ""}
                  />
                </div>
              </div>

              {/* <div className="twm-advertisment">
                <div className="overlay"></div>
                <h4 className="twm-title">Recruiting?</h4>
                <p>
                  Get Best Matched Jobs On your Email. <br />
                  Add Resume NOW !
                </p>
              </div>

              <div className="bigyapan">
                <Link to="/">
                  <figure>
                    <img src="portal/images/interniship.png" alt="#" />
                  </figure>
                </Link>
              </div> */}
            </div>

            <div className="col-xl-9 col-lg-8 col-md-12">
              {renderActiveFilters()}

              <div className="row" key={"jobListDiv"}>
                {homejobsArray.length > 0 ? (
                  homejobsArray.map((jobitem) => {
                    let rnd = generateRandomString(8);
                    // return (
                    //   <div className="col-lg-6 col-md-12 m-b30" key={`jobList${rnd}`}>
                    //     <JobItem key={jobitem.id} job={jobitem} />
                    //   </div>
                    // );

                    return (
                      <div className="col-lg-6 col-md-12 m-b30" key={`jobList_${jobitem.id}`}>
                        <JobItem key={jobitem.id} job={jobitem} />
                      </div>
                    );
                  })
                ) : !isLoading ? (
                  <div className="col-xl-12 col-lg-12 col-md-12 text-center p-t40 p-b40" style={{ width: "100%" }}>
                    <div className="sf-no-jobs-alert" style={{
                      padding: "30px",
                      background: "#fff",
                      borderRadius: "12px",
                      border: "1px dashed #c3cddc",
                      color: "#4a5565",
                      fontSize: "1.1rem",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.02)",
                      width: "100%"
                    }}>
                      <i className="ri-information-line" style={{
                        fontSize: "2rem",
                        color: "#2e3192",
                        display: "block",
                        marginBottom: "10px"
                      }}></i>
                      <span>
                        {Object.keys(searchParams).length > 0 ? (
                          <>
                            No jobs found for the filter: <strong style={{ color: "#111" }}>{getActiveFiltersString() || "active criteria"}</strong>
                          </>
                        ) : (
                          "No jobs found."
                        )}
                      </span>
                    </div>
                  </div>
                ) : (
                  ""
                )}
                <div className="clearfix"></div>
              </div>

              {renderPagination()}
            </div>
          </div>
        </div>
      </div>

      {/* <div className="section-full site-bg-light-purple twm-for-employee-4">
        <div className="container">
          <div className="section-content">
            <div className="twm-for-employee-content">
              <div className="row">
                {aboutArray.length > 0
                  ? aboutArray.map((item, key) => (
                      <div className="col-xl-5 col-lg-12 col-md-12" key={key}>
                        <div className="twm-explore-content-outer2">
                          <div className="twm-explore-top-section">
                            <div className="section-head left wt-small-separator-outer">
                              <div className="wt-small-separator site-text-primary">
                                <div>About </div>
                              </div>
                              <h2>{item.page_title}</h2>
                              <p>{item.short_content}</p>
                            </div>
                            <div className="twm-read-more">
                              <Link to={`/about`} className="site-button">
                                Read More
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  : ""}
                <div className="col-xl-7 col-lg-12 col-md-12">
                  <div className="twm-explore-right-section">
                    <div className="twm-media">
                      <div className="twm-employee-pic">
                        <img src="portal/images/sangita.png" alt="" />
                      </div>
                    </div>
                    {organizationArray.length > 0
                      ? organizationArray.map((item, key) => (
                          <div key={key}>
                            <div className="counter-outer-two one anm" data-speed-y="-2" data-speed-scale="15" data-speed-opacity="1">
                              <div className="icon-content">
                                <div className="tw-count-number text-clr-purple">
                                  <span className="counter">{item.total_jobseeker}</span>
                                </div>
                                <p className="icon-content-info">Total Jobseeker</p>
                              </div>
                            </div>
                            <div className="counter-outer-two two anm" data-speed-y="2" data-speed-scale="15" data-speed-opacity="5">
                              <div className="icon-content">
                                <div className="tw-count-number text-clr-sky">
                                  <span className="counter">{item.total_jobs}</span>
                                </div>
                                <p className="icon-content-info">Total job posted</p>
                              </div>
                            </div>
                            <div className="counter-outer-two three anm" data-speed-x="-4" data-speed-scale="-25">
                              <div className="icon-content">
                                <div className="tw-count-number text-clr-pink">
                                  <span className="counter">{item.total_employer}</span>
                                </div>
                                <p className="icon-content-info">Total employee</p>
                              </div>
                            </div>
                          </div>
                        ))
                      : ""}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> */}

      {isScrollVisible && (
        <button
          className="scroltop"
          onClick={scrollToTop}
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            backgroundColor: "#6c63ff",
            color: "#fff",
            border: "none",
            borderRadius: "50%",
            width: "45px",
            height: "45px",
            fontSize: "20px",
            cursor: "pointer",
            boxShadow: "0px 4px 8px rgba(0,0,0,0.2)",
            zIndex: 999,
          }}
        >
          <span className="fa fa-angle-up relative" id="btn-vibrate"></span>
        </button>
      )}

      {/* Mobile Filter Sidebar Drawer */}
      {windowWidth <= 992 && (
        <React.Fragment>
          {/* Mobile Filter Sidebar Overlay */}
          <div
            className={`mobile-filter-sidebar-overlay ${isFilterMenuOpen ? "active" : ""}`}
            onClick={() => setIsFilterMenuOpen(false)}
          ></div>

          {/* Mobile Left-Slide Filter Sidebar Drawer */}
          <div className={`mobile-filter-sidebar ${isFilterMenuOpen ? "active" : ""}`}>
            <div className="mobile-filter-sidebar-header">
              {/* <h4>Filter Jobs</h4> */}
              <button
                className="mobile-filter-sidebar-close"
                onClick={() => setIsFilterMenuOpen(false)}
                aria-label="Close Filter"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="mobile-filter-sidebar-body">
              <div className="side-bar">
                <div className="sidebar-elements search-bx">
                  <JobFilterForm 
                    searchParams={searchParams}
                    initialTypeOfWork={searchParams.typeOfWork || ""}
                    initialJobType={searchParams.jobType || ""}
                    initialPostDate={searchParams.postDate || ""}
                  />
                </div>
              </div>
            </div>
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
}
